// Path: src/components/auth/register/OLSSRegister.tsx
// Added referral code parsing from URL and manual referral input field
// Tracks referrals in Firestore and applies 5% commission for referrers
// Path: src/components/auth/register/OLSSRegister.tsx
// Added Ant Design icons to form fields for visual clarity
// Updated Breadcrumb to use items API per Ant Design deprecation warning
// Improved accessibility with ARIA labels
// Enhanced SEO metadata with updated keywords and structured data
// 05/31/25 06:21 PM +07
// Path: src/components/auth/register/OLSSRegister.tsx
// Removed BackButton component for a streamlined registration flow
// Reintroduced logo preload with proper attributes
// Ensured accessibility and SEO alignment
// 05/31/25 06:27 PM +07
import React, { useState, useEffect, ChangeEvent } from "react";
import { withFirebase, WithFirebaseProps } from "../../dev/Firebase/Firebase";
import { UserRole } from "../../../constants/UserEnums";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  TimePicker,
  Tooltip,
  Typography,
  message,
  RadioChangeEvent,
  Divider,
  Breadcrumb,
} from "antd";
import {
  MailOutlined,
  LockOutlined,
  UserOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  IdcardOutlined,
  MedicineBoxOutlined,
  BookOutlined,
  TeamOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useUserStore } from "../../../stores/UserStore";
import Spinner from "../../dev/utils/Spinner";
import moment, { Moment } from "moment";
import { Helmet } from "react-helmet-async";
import { doc, setDoc, collection, getDocs, increment, updateDoc } from "firebase/firestore";
import "./OLSSRegister.css";

const { Title, Text } = Typography;

// Define the form values type
interface OLSSFormValues {
  email: string;
  password?: string;
  confirm?: string;
  name: string;
  reference: string;
  referralCode?: string;
  age: number | null;
  credits: string[];
  health: string;
  location: string;
  meditationExperience: string;
  olsExperience: string;
  teacher: string;
  start: Moment;
  time: Moment;
  agreement1: boolean;
  agreement2: boolean;
}

// Define the state type
interface OLSSState {
  confirmDirty: boolean;
  radioValue0: string;
  checkboxOther: string;
  checkboxOtherChecked: boolean;
  isSubmitting: boolean;
}

const RegisterContainer = styled.div`
  animation: fadeIn 1.5s ease-in;
  background: linear-gradient(135deg, #FFC107 0%, #FFCA28 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  width: 100%;
  box-sizing: border-box;
  position: relative;
`;

const RegisterCard = styled.div`
  width: 95%;
  max-width: 100%;
  margin: 0 auto;
  background: #fce248;
  box-shadow: 0 6px 20px rgba(255, 204, 0, 0.4);
  border: 1px solid #ffeb3b;
  border-radius: 12px;
  padding: 40px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    width: 96% !important;
    padding: 25px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const StyledForm = styled(Form)`
  padding: 24px;

  .ant-form-item-label > label {
    font-size: 18px;
    color: #0427f4;
    font-weight: 500;
  }

  .ant-form-item {
    margin-bottom: 20px;
  }

  .ant-input,
  .ant-input-number,
  .ant-picker,
  .ant-time-picker {
    border-radius: 8px;
    padding: 8px;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
  }

  .ant-input:hover,
  .ant-input-number:hover,
  .ant-picker:hover,
  .ant-time-picker:hover {
    border-color: #40c4ff;
  }

  .ant-input:focus,
  .ant-input-number:focus,
  .ant-picker:focus,
  .ant-time-picker:focus {
    border-color: #40c4ff;
    box-shadow: 0 0 0 2px rgba(64, 196, 255, 0.2);
  }

  .ant-radio-wrapper,
  .ant-checkbox-wrapper {
    font-size: 16px;
    color: #0427f4;
    transition: transform 0.3s ease;
  }

  .ant-radio-wrapper:hover,
  .ant-checkbox-wrapper:hover {
    transform: translateX(5px);
  }

  .ant-radio-wrapper:focus-within,
  .ant-checkbox-wrapper:focus-within {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  .ant-btn {
    font-size: 18px;
    border-radius: 8px;
    padding: 12px 24px;
    width: 100%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .ant-btn:hover,
  .ant-btn:focus {
    transform: scale(1.02);
  }

  .ant-btn:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: 15px;

    .ant-form-item-label > label,
    .ant-input,
    .ant-input-number,
    .ant-picker,
    .ant-time-picker,
    .ant-radio-wrapper,
    .ant-checkbox-wrapper,
    .ant-btn {
      font-size: 14px !important;
    }
  }

  @media (max-width: 480px) {
    .ant-form-item-label > label {
      font-size: 16px !important;
    }

    .ant-input,
    .ant-input-number,
    .ant-picker,
    .ant-time-picker,
    .ant-radio-wrapper,
    .ant-checkbox-wrapper,
    .ant-btn {
      font-size: 12px !important;
    }
  }
`;

const CreditsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
  padding: 10px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const SectionDivider = styled(Divider)`
  .ant-divider-inner-text {
    font-size: 20px;
    color: #0427f4;
    font-weight: 500;
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const radioStyle: React.CSSProperties = {
  display: "block",
  height: "40px",
  lineHeight: "40px",
  color: "#0427f4",
};

const OLSSRegister: React.FC<WithFirebaseProps> = ({ firebase }) => {
  const [form] = Form.useForm<OLSSFormValues>();
  const [state, setState] = useState<OLSSState>({
    confirmDirty: false,
    radioValue0: "New",
    checkboxOther: "",
    checkboxOtherChecked: false,
    isSubmitting: false,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const userStore = useUserStore();

  // Parse referral code from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get("ref");
    if (refCode) {
      form.setFieldsValue({ referralCode: refCode });
      console.log("Referral code from URL:", refCode);
    }
  }, [location, form]);

  useEffect(() => {
    if (!userStore.isLoaded) return;
    const user = userStore.user;
    const initialValues: OLSSFormValues = user
      ? {
          email: user.email || "",
          name: user.name || "",
          reference: user.reference || "",
          referralCode: user.referralCode || "",
          age: user.age || null,
          credits: user.credits || [],
          health: user.health || "",
          location: user.loc || "",
          meditationExperience: user.meditationExperience || "",
          olsExperience: user.olsExperience || "New",
          teacher: user.teacher || "OLS eae | Asvitloaten Egypt / UA",
          start: user.start ? moment(user.start) : moment(),
          time: user.start ? moment(user.start) : moment(),
          agreement1: user.agreement1 || false,
          agreement2: user.agreement2 || false,
        }
      : {
          email: "",
          name: "",
          reference: "",
          referralCode: "",
          age: null,
          credits: [],
          health: "",
          location: "",
          meditationExperience: "",
          olsExperience: "New",
          teacher: "OLS eae | Asvitloaten Egypt / UA",
          start: moment(),
          time: moment(),
          agreement1: false,
          agreement2: false,
        };
    setState((prev) => ({
      ...prev,
      radioValue0: initialValues.olsExperience,
    }));
    form.setFieldsValue(initialValues);
    console.log("Form initialized with values:", initialValues);
  }, [userStore.isLoaded, userStore.user, form]);

  const handleSubmit = async (values: OLSSFormValues) => {
    console.log("Submitting OLSS registration with values:", values);
    setState((prev) => ({ ...prev, isSubmitting: true }));
    const { email, password, referralCode } = values;

    try {
      if (!firebase) {
        throw new Error("Firebase is not initialized");
      }

      const userData = {
        name: values.name,
        reference: values.reference,
        referralCode: referralCode || "",
        age: values.age,
        credits: values.credits || [],
        health: values.health,
        loc: values.location,
        meditationExperience: values.meditationExperience,
        olsExperience: values.olsExperience,
        teacher: values.teacher,
        start: values.start.format("YYYY-MM-DD"),
        time: values.time.format("h:mm a"),
        userRole: UserRole.OLSS_COL,
        agreement1: values.agreement1,
        agreement2: values.agreement2,
      };

      if (!userStore.isLoggedIn && email && password) {
        console.log("Creating new user with:", email);
        const userCredential = await firebase.createUser(email, password);
        const user = userCredential.user;
        console.log("User created:", user.uid);

        userData.email = user.email;
        userData.uid = user.uid;
        userData.createdAt = firebase.serverTimestamp();
        const userDocRef = firebase.doc("users", user.uid);
        await firebase.setDoc(userDocRef, userData);
        console.log("User data saved to Firestore:", user.uid);

        if (referralCode) {
          const db = firebase.firestore;
          const referralCollection = collection(db, `referral_links`);
          const referralDocs = await getDocs(referralCollection);
          let referrerUid: string | null = null;

          referralDocs.forEach((doc) => {
            const data = doc.data();
            if (data.link.includes(referralCode)) {
              referrerUid = doc.ref.parent.parent?.id || null;
            }
          });

          if (referrerUid) {
            const conversionRef = doc(collection(db, `referral_conversions`));
            await setDoc(conversionRef, {
              referrerUid,
              referredUid: user.uid,
              referralCode,
              commission: 0.05,
              createdAt: new Date(),
            });
            console.log("Referral conversion recorded:", { referrerUid, referredUid: user.uid, referralCode });

            const referralDoc = doc(db, `referral_links/${referrerUid}/codes`, referralCode);
            await updateDoc(referralDoc, {
              conversions: increment(1),
            });
          } else {
            console.warn("No matching referrer found for referral code:", referralCode);
          }
        }

        try {
          await firebase.sendEmailVerification(user, {
            url: "https://www.olsme.com/login",
          });
          console.log("Verification email sent to:", user.email);
          message.success("Registration successful! Please check your email (including spam) to verify your account.");
        } catch (emailError: any) {
          console.error("Email verification error:", emailError.code, emailError.message);
          message.warning("Registration successful, but verification email failed. Check spam or verify manually in your profile.");
        }

        const userDoc = await firebase.getDoc(userDocRef);
        const fullUserData = userDoc.data();
        userStore.setUser({
          ...fullUserData,
          emailVerified: user.emailVerified,
        });
        console.log("UserStore synced with full data:", fullUserData);
        navigate("/login");
      } else if (userStore.isLoggedIn) {
        console.log("Updating user:", userStore.user.uid);
        const userDocRef = firebase.doc("users", userStore.user.uid);
        await firebase.updateDoc(userDocRef, userData);
        userStore.setUser({ ...userStore.user, ...userData });
        message.success("Account updated successfully!");
        navigate("/home/profile/verification");
      } else {
        throw new Error("Invalid submission state: missing email/password for new user");
      }
    } catch (error: any) {
      console.error("OLSS Registration error:", error.code || "undefined", error.message || error);
      message.error(`Registration failed: ${error.message || error.code || "Please try again."}`);
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleCheckboxOther = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState({ ...state, checkboxOther: value, checkboxOtherChecked: !!value });
    const currentCredits = form.getFieldValue("credits") || [];
    form.setFieldsValue({
      credits: [...currentCredits.filter((c: string) => !c.startsWith("Other ")), value ? "Other " + value : null].filter(Boolean),
    });
  };

  const compareToFirstPassword = (_: any, value: string) => {
    if (value && value !== form.getFieldValue("password")) {
      return Promise.reject("Passwords do not match!");
    }
    return Promise.resolve();
  };

  const validateToNextPassword = (_: any, value: string) => {
    if (value && state.confirmDirty) {
      form.validateFields(["confirm"]);
    }
    return Promise.resolve();
  };

  const onChange = (e: RadioChangeEvent) => {
    setState({ ...state, radioValue0: e.target.value });
    form.setFieldsValue({ olsExperience: e.target.value });
  };

  const handleFinishFailed = (errorInfo: any) => {
    console.log("Form validation failed:", errorInfo);
    const errorFields = errorInfo.errorFields.map((field: any) => `${field.name}: ${field.errors.join(", ")}`);
    console.log("Specific validation errors:", errorFields);
    message.error(`Please fill in all required fields correctly: ${errorFields.join("; ")}`);
  };

  const handleSubmitClick = () => {
    const currentValues = form.getFieldsValue(true);
    console.log("Submit button clicked, current form values:", currentValues);

    const requiredFields = [
      "email",
      "password",
      "confirm",
      "name",
      "reference",
      "age",
      "health",
      "location",
      "meditationExperience",
      "olsExperience",
      "teacher",
      "start",
      "time",
      "agreement1",
      "agreement2",
    ];
    const missingFields = requiredFields.filter((field) => !currentValues[field] && currentValues[field] !== false);
    if (missingFields.length > 0) {
      console.log("Missing required fields:", missingFields);
      message.error(`Please fill in all required fields: ${missingFields.join(", ")}`);
      return;
    }

    form
      .validateFields()
      .then((values) => {
        console.log("Validation succeeded, submitting:", values);
        handleSubmit(values);
      })
      .catch((errorInfo) => {
        console.log("Ant Design validation failed, forcing submission if all fields present:", errorInfo);
        if (errorInfo.errorFields.length === 0 && missingFields.length === 0) {
          console.log("Forcing submission due to outOfDate issue:", currentValues);
          handleSubmit(currentValues);
        } else {
          handleFinishFailed(errorInfo);
        }
      });
  };

  if (!userStore.isLoaded) {
    return <Spinner />;
  }

  return (
    <RegisterContainer>
      <Helmet>
        <title>OLS Student Registration | onelightsystem OLS - Sun Light Meditation</title>
        <meta
          name="description"
          content="Join onelightsystem OLS to start your Sun Light Meditation journey. Register now for empowering courses and connect with a global Light Civilization!"
        />
        <meta
          name="keywords"
          content="OLS student registration, Sun Light Meditation, OLS meditation education, Light Civilization, onelightsystem OLS, meditation courses, spiritual growth, join OLS"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLS Student Registration | onelightsystem OLS - Sun Light Meditation" />
        <meta
          property="og:description"
          content="Join onelightsystem OLS to start your Sun Light Meditation journey. Register now for empowering courses and connect with a global Light Civilization!"
        />
        <meta property="og:url" content="https://www.olsme.com/register/olssregister" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="onelightsystem OLS Logo for Student Registration Page" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="OLS Student Registration | onelightsystem OLS - Sun Light Meditation" />
        <meta
          name="twitter:description"
          content="Join onelightsystem OLS to start your Sun Light Meditation journey. Register now for empowering courses and connect with a global Light Civilization!"
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="onelightsystem OLS Logo for Student Registration Page" />
        <link rel="canonical" href="https://www.olsme.com/register/olssregister" />
        <link rel="preload" href="/logo.jpg" as="image" type="image/jpeg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "OLS Student Registration",
            "url": "https://www.olsme.com/register/olssregister",
            "description": "Join onelightsystem OLS to start your Sun Light Meditation journey. Register now for empowering courses and connect with a global Light Civilization.",
            "mainEntity": {
              "@type": "Service",
              "name": "OLS Student Registration",
              "description": "Registration service for onelightsystem OLS students, offering Sun Light Meditation education and Light Civilization community access.",
              "provider": {
                "@type": "Organization",
                "name": "onelightsystem OLS",
                "url": "https://www.olsme.com",
                "logo": "https://www.olsme.com/logo.jpg",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer service",
                  "email": "info@olsme.com",
                  "telephone": "+1-312-945-6172",
                },
                "sameAs": [
                  "https://www.linkedin.com/company/onelightsystem",
                  "https://twitter.com/onelightsystem",
                  "https://www.facebook.com/onelightsystem",
                  "https://www.instagram.com/onelightsystem",
                  "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
                ],
              },
            },
            "publisher": {
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
              "logo": "https://www.olsme.com/logo.jpg",
            },
            "offers": {
              "@type": "Offer",
              "name": "OLS Student Registration",
              "description": "Introductory and initiation evaluation for Sun Light Meditation training.",
              "url": "https://www.olsme.com/register/olssregister",
              "availability": "https://schema.org/InStock",
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Register",
                  "item": "https://www.olsme.com/register",
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "OLS Student Registration",
                  "item": "https://www.olsme.com/register/olssregister",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb
          items={[
            {
              title: (
                <RouterLink to="/">
                  <HomeOutlined /> Home
                </RouterLink>
              ),
            },
            {
              title: <RouterLink to="/register">Register</RouterLink>,
            },
            {
              title: "OLS Student Registration",
            },
          ]}
        />
      </BreadcrumbWrapper>
      <RegisterCard>
        <StyledForm
          form={form}
          onFinish={handleSubmit}
          onFinishFailed={handleFinishFailed}
          layout="vertical"
          className="ols-student-form"
        >
          <div style={{ color: "#0427f4", textAlign: "center", marginBottom: "20px" }}>
            <Title style={{ color: "#0427f4" }} level={1}>
              OLS Student Evaluation Tuition Services Application
            </Title>
            <Text style={{ color: "#0427f4", display: "block", marginBottom: "20px" }}>
              Complete the form below to join onelightsystem OLS as a student
            </Text>
            <img
              src="/logo.jpg"
              alt="onelightsystem OLS Logo"
              style={{ width: "150px", margin: "0 auto 20px", display: "block" }}
            />
          </div>

          <SectionDivider orientation="left">
            <UserOutlined style={{ marginRight: 8 }} /> Personal Information
          </SectionDivider>
          <Form.Item
            label={<span>E-mail</span>}
            name="email"
            rules={[
              { type: "email", message: "Invalid email address!" },
              { required: true, message: "Email is required!" },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              disabled={userStore.isLoggedIn}
              aria-label="Email address"
            />
          </Form.Item>
          {!userStore.isLoggedIn && (
            <>
              <Form.Item
                label={<span>Password</span>}
                name="password"
                rules={[
                  { required: true, message: "Password is required!" },
                  { min: 6, message: "Password must be at least 6 characters!" },
                  { validator: validateToNextPassword },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  aria-label="Password"
                />
              </Form.Item>
              <Form.Item
                label={<span>Confirm Password</span>}
                name="confirm"
                rules={[
                  { required: true, message: "Confirm your password!" },
                  { validator: compareToFirstPassword },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  onBlur={(e) => setState({ ...state, confirmDirty: !!e.target.value })}
                  aria-label="Confirm password"
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            label={<span>Name</span>}
            name="name"
            rules={[{ required: true, message: "Name is required!" }]}
          >
            <Input
              prefix={<UserOutlined />}
              aria-label="Full name"
            />
          </Form.Item>
          <Form.Item
            label={<span>Location (City, Country)</span>}
            name="location"
            rules={[{ required: true, message: "Location is required!" }]}
            tooltip={{
              title: "Determines OLS student service tuition value fees under GDP value evaluation",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input
              prefix={<GlobalOutlined />}
              aria-label="Location (City, Country)"
            />
          </Form.Item>
          <Form.Item
            label={<span>Age</span>}
            name="age"
            rules={[{ required: true, message: "Age is required!", type: "number", min: 6, max: 120 }]}
            tooltip={{
              title: "Ages 6-12 require a parent, 13-16 (category II), 17-21 (III), 22-27 (4), 28-32 (5), 33-39 (6), 40-44 (7), 45-49 (8), 50-55 (9), 56-62 (10), 63-69 (11), 70-77(12), 78-85 (13), 86 up (14)",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <InputNumber
              prefix={<IdcardOutlined />}
              style={{ width: "100%" }}
              aria-label="Age"
            />
          </Form.Item>
          <Form.Item
            label={<span>Any Health Concerns</span>}
            name="health"
            rules={[{ required: true, message: "Health information is required!" }]}
          >
            <Input
              prefix={<MedicineBoxOutlined />}
              aria-label="Health concerns"
            />
          </Form.Item>

          <SectionDivider orientation="left">
            <InfoCircleOutlined style={{ marginRight: 8 }} /> Experience and Referral
          </SectionDivider>
          <Form.Item
            label={<span>Onelightsystem OLS Experience</span>}
            name="olsExperience"
            rules={[{ required: true, message: "OLS experience is required!" }]}
          >
            <Radio.Group onChange={onChange} value={state.radioValue0}>
              <Radio style={radioStyle} value="New">New</Radio>
              <Radio style={radioStyle} value="PAST Initiated">PAST Initiated</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={<span>How Did You Find Out About OLS</span>}
            name="reference"
            rules={[{ required: true, message: "Referral information is required!" }]}
            tooltip={{
              title: "Was it through an OLS promoter or referral?",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input
              prefix={<TeamOutlined />}
              aria-label="How did you find out about OLS"
            />
          </Form.Item>
          <Form.Item
            label={<span>Referral Code (Optional)</span>}
            name="referralCode"
            tooltip={{
              title: "Enter a referral code to receive a 5% commission on your registration fees.",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter referral code"
              aria-label="Referral code"
            />
          </Form.Item>
          <Form.Item
            label={<span>Meditation Experience</span>}
            name="meditationExperience"
            rules={[{ required: true, message: "Meditation experience is required!" }]}
            tooltip={{
              title: "Describe your meditation practices, duration, and specifics.",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Input
              prefix={<BookOutlined />}
              aria-label="Meditation experience"
            />
          </Form.Item>
          <Form.Item
            label={<span>OLS Teacher/Instructor</span>}
            name="teacher"
            rules={[{ required: true, message: "OLS official is required!" }]}
            tooltip={{
              title: "Select your OLS official: OLS eae (teacher) or OLS inuo (instructor). Each has its own evaluation protocols for service fees.",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Radio.Group>
              <Radio style={radioStyle} value="OLS eae | Asvitloaten Egypt / UA">
                OLS eae | Asvitloaten Egypt / UA
              </Radio>
              <Radio style={radioStyle} value="OLS inuo | Rei-Wen Ho / CA USA / Taiwan">
                OLS inuo | Rei-Wen Ho / CA USA / Taiwan
              </Radio>
            </Radio.Group>
          </Form.Item>

          <SectionDivider orientation="left">
            <CalendarOutlined style={{ marginRight: 8 }} /> Scheduling
          </SectionDivider>
          <Form.Item
            label={<span>Schedule OLS Intro</span>}
            name="start"
            rules={[{ required: true, message: "Date is required!" }]}
          >
            <DatePicker
              prefix={<CalendarOutlined />}
              style={{ width: "100%" }}
              aria-label="Schedule OLS intro date"
            />
          </Form.Item>
          <Form.Item
            label={<span>Select a Day Time</span>}
            name="time"
            rules={[{ required: true, message: "Time is required!" }]}
            tooltip={{
              title: "Provide an approximate time; exact scheduling follows evaluation and 50% pre-payment.",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <TimePicker
              prefix={<ClockCircleOutlined />}
              use12Hours
              format="h:mm a"
              style={{ width: "100%" }}
              aria-label="Select a day time"
            />
          </Form.Item>

          <SectionDivider orientation="left">Agreements</SectionDivider>
          <Form.Item
            label={<span>Application Validity</span>}
            name="agreement1"
            valuePropName="checked"
            rules={[{ required: true, message: "Please agree to validate information!" }]}
            tooltip={{
              title: "OLS service fees are based on evaluation. Valid for 9 days; re-evaluation increases fees by 10%. Expect an email from intro@ols-med.net with assessed fees, including 50% pre-payment for scheduling.",
              icon: <QuestionCircleOutlined />,
            }}
          >
            <Checkbox>All the information submitted in this form is true and correct</Checkbox>
          </Form.Item>
          <Form.Item
            label={<span>OLS Private Privacy Disclosure Agreement</span>}
            name="agreement2"
            valuePropName="checked"
            rules={[{ required: true, message: "Please agree to the privacy terms!" }]}
          >
            <Checkbox>
              I have read and agreed to the{" "}
              <RouterLink to="/OLSsPrivacy" style={{ color: "#3552fc" }}>
                OLS student terms of service
              </RouterLink>
            </Checkbox>
          </Form.Item>

          <SectionDivider orientation="left">Evaluation Credits</SectionDivider>
          <Form.Item
            label={<span>Select Evaluation Credits: Are in % and/or $ Depending on Your Location and Evaluation Conditions</span>}
            name="credits"
          >
            <Checkbox.Group>
              <CreditsGrid>
                <Checkbox value="10 Google Review">
                  <Tooltip title="Earn 10% credit by leaving a review if you’ve had prior OLS experience.">
                    <span style={{ color: "#0427f4" }}>
                      (4) Had prior OLS experience?! Leave a review on{" "}
                      <a href="https://g.page/r/CSXMIogquoq1EB0/review" style={{ color: "#3552fc" }}>Google</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="1 Lessons.com">
                  <Tooltip title="Earn 1% credit by leaving a review if you’ve had prior OLS experience.">
                    <span style={{ color: "#0427f4" }}>
                      (2) Had prior OLS experience?! Leave a review on{" "}
                      <a href="https://goo.gl/dhbvmX" style={{ color: "#3552fc" }}>Lessons</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="4 Youtube">
                  <Tooltip title="Earn 4% credit by subscribing to our YouTube channel.">
                    <span style={{ color: "#0427f4" }}>
                      (3) Subscribed on{" "}
                      <a href="https://www.youtube.com/c/ONELIGHTSYSTEMOLSMeditation" style={{ color: "#3552fc" }}>YouTube</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="5 Odysee">
                  <Tooltip title="Earn 5% credit by signing up and following us on Odysee.">
                    <span style={{ color: "#0427f4" }}>
                      (5) Signup to Follow on{" "}
                      <a href="https://odysee.com/$/invite/@onelightsystemOLS:f" style={{ color: "#3552fc" }}>Odysee</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="2 Blog">
                  <Tooltip title="Earn 2% credit by subscribing to our blog.">
                    <span style={{ color: "#0427f4" }}>
                      (3) Subscribed on{" "}
                      <a href="https://onelightsystem.blogspot.com/" style={{ color: "#3552fc" }}>Blog</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="5 CrunchBase">
                  <Tooltip title="Earn 5% credit by following us on CrunchBase.">
                    <span style={{ color: "#0427f4" }}>
                      (3) Followed us on{" "}
                      <a href="https://www.crunchbase.com/organization/onelightsystem-ols" style={{ color: "#3552fc" }}>CrunchBase</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="5 Instagram">
                  <Tooltip title="Earn 5% credit by following us on Instagram (for active OLS members only).">
                    <span style={{ color: "#0427f4" }}>
                      (1) Followed us on (only for active OLS members){" "}
                      <a href="https://www.instagram.com/onelightsystem_ols/" style={{ color: "#3552fc" }}>Instagram</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="5 Twitter">
                  <Tooltip title="Earn 5% credit by following us on Twitter.">
                    <span style={{ color: "#0427f4" }}>
                      (4) Followed us on{" "}
                      <a href="https://www.twitter.com/onelightsystem" style={{ color: "#3552fc" }}>Twitter</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="5 LinkedIn">
                  <Tooltip title="Earn 5% credit by following us on LinkedIn.">
                    <span style={{ color: "#0427f4" }}>
                      (5) Followed us on{" "}
                      <a href="https://www.linkedin.com/company/one-light-system/" style={{ color: "#3552fc" }}>LinkedIn</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="5 Owler">
                  <Tooltip title="Earn 5% credit by following us on Owler and weighing in.">
                    <span style={{ color: "#0427f4" }}>
                      (3) Followed us on{" "}
                      <a href="https://www.owler.com/company/ols-med" style={{ color: "#3552fc" }}>Owler and Weigh in</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="Udemy">
                  <Tooltip title="Earn 400% credit by taking our prep class on Udemy.">
                    <span style={{ color: "#0427f4" }}>
                      (400) Took our prep. class on{" "}
                      <a href="https://www.udemy.com/onelightsystem-olsm/?instructorPreviewMode=guest" style={{ color: "#3552fc" }}>Udemy</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="5 Facebook">
                  <Tooltip title="Earn 5% credit by liking and sharing our Facebook pages.">
                    <span style={{ color: "#0427f4" }}>
                      (10) Liked and shared our{" "}
                      <a href="https://www.facebook.com/onelightsystem/" style={{ color: "#3552fc" }}>Facebook 1</a>,{" "}
                      <a href="https://www.facebook.com/onelightsystemols/" style={{ color: "#3552fc" }}>Fb2</a> 1st and/or 2 pages
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="Offer something else">
                  <Tooltip title="Suggest another way to earn credits.">
                    <span style={{ color: "#0427f4" }}>I can offer something else</span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="successful referral">
                  <Tooltip title="Earn 10% credit by referring a new OLS student.">
                    <span style={{ color: "#0427f4" }}>
                      Recommend to a new prospect to become an OLS student and get 10% referral
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="1 Latium">
                  <Tooltip title="Earn 1% credit by registering and getting verified on Latium.">
                    <span style={{ color: "#0427f4" }}>
                      (20) Register and get Verified{" "}
                      <a href="https://latium.org/welcome?welcome_id=o43XwMg1" style={{ color: "#3552fc" }}>Latium</a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="get the app and mine Pi">
                  <Tooltip title="Earn 10% credit by mining Pi digital currencies using my username.">
                    <span style={{ color: "#0427f4" }}>
                      (10) Mine Pi digital currencies{" "}
                      <a href="https://minepi.com/asvitloaten" style={{ color: "#3552fc" }}>
                        download the app, use my username (asvitloaten)
                      </a>
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="Pay with crypto">
                  <Tooltip title="Earn 10% credit by paying OLS service fees with valid cryptocurrencies.">
                    <span style={{ color: "#0427f4" }}>
                      (10) Will apply valid crypto currencies to pay for OLS service fees
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value="Create OLS Video">
                  <Tooltip title="Earn 20% credit by creating and sharing a video about your OLS experience.">
                    <span style={{ color: "#0427f4" }}>
                      (20) I will create a video on the OLS experience and share it to social
                    </span>
                  </Tooltip>
                </Checkbox>
                <Checkbox value={"Other " + state.checkboxOther} checked={state.checkboxOtherChecked}>
                  <Tooltip title="Specify another credit option.">
                    <span style={{ color: "#0427f4" }}>Other: </span>
                    <Input
                      style={{ width: 150, marginLeft: 10, borderRadius: 8, padding: "8px" }}
                      onChange={handleCheckboxOther}
                      value={state.checkboxOther}
                      aria-label="Other credit option"
                    />
                  </Tooltip>
                </Checkbox>
              </CreditsGrid>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              onClick={handleSubmitClick}
              loading={state.isSubmitting}
              aria-label={userStore.isLoggedIn ? "Update account" : "Apply for OLS student"}
            >
              {userStore.isLoggedIn ? "Update Account" : "Apply for OLS Student"}
            </Button>
          </Form.Item>
          <Text style={{ color: "#0427f4", marginTop: "20px", display: "block", textAlign: "center" }}>
            Need help?{" "}
            <RouterLink to="/contact" style={{ color: "#3552fc", textDecoration: "underline" }}>
              Contact us
            </RouterLink>{" "}
            or learn more about{" "}
            <RouterLink to="/olsme" style={{ color: "#3552fc", textDecoration: "underline" }}>
              OLSME Subscriptions
            </RouterLink>
          </Text>
        </StyledForm>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default withFirebase(OLSSRegister);